import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from '../common/ids.js';
import { PunchTypeSchema, PunchSourceSchema, GpsStatusSchema, GeoResultSchema, ExceptionTypeSchema, } from '../common/enums.js';
import { PaginationQuerySchema } from '../common/pagination.js';
import { DiscoveryQuerySchema } from '../common/discovery.js';
/** D-10 offline payload — the raw device timing evidence for `resolveTrustedTime()`. */
export const CapturedAtSchema = z.object({
    deviceRecordedAt: IsoDateTimeSchema,
    /**
     * Optional (Phase 1 change batch, 2026-08-27, Track C): the monotonic
     * anchor is lost after an app restart. When absent, the server falls back
     * to `deviceRecordedAt` for `resolveTrustedTime()` and always raises
     * `CLOCK_DRIFT` — see `03-API-CONTRACT.md` §3.
     */
    monotonicElapsedMs: z.int().min(0).optional(),
    clockOffsetMs: z.int(),
    lastServerContactAt: IsoDateTimeSchema,
});
export const GpsSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracyM: z.number().min(0),
});
const punchBaseShape = {
    clientPunchUuid: UuidSchema,
    deviceUuid: UuidSchema,
    capturedAt: CapturedAtSchema.optional(),
    gps: GpsSchema.optional(),
    gpsStatus: GpsStatusSchema.optional(),
};
/**
 * `gps` XOR `gpsStatus` (04-PUNCH-ENGINE.md §5: geofence evaluation needs
 * either coordinates or the documented reason they are missing — never
 * neither, never both).
 */
const gpsXorGpsStatus = (v) => (v.gps != null) !== (v.gpsStatus != null);
const gpsXorGpsStatusRefinement = {
    error: 'Exactly one of gps or gpsStatus must be provided',
    path: ['gps'],
};
export const PunchInRequestSchema = z
    .object({ ...punchBaseShape, shiftId: UuidSchema })
    .refine(gpsXorGpsStatus, gpsXorGpsStatusRefinement);
/** No `shiftId` — punch OUT resolves the open session from `ctx.userId` (04-PUNCH-ENGINE.md §4). */
export const PunchOutRequestSchema = z
    .object({
    ...punchBaseShape,
    /**
     * Phase 5 (tasks, 2026-09-02, T-07): why the worker is punching out with a
     * blocking task still open. Optional, and when present the OUT is
     * **always** accepted — a refused OUT strands an open session and there is
     * no auto-close job (D-08) and no session-close endpoint. The bypass is
     * recorded as a `PUNCH_OUT_TASKS_BYPASSED` exception (high) instead.
     */
    taskOverrideReason: z.string().trim().min(10).optional(),
})
    .refine(gpsXorGpsStatus, gpsXorGpsStatusRefinement);
export const PunchResultResponseSchema = z.object({
    punchId: UuidSchema,
    sessionId: UuidSchema,
    recordedAt: IsoDateTimeSchema,
    source: PunchSourceSchema,
    geoResult: GeoResultSchema,
    /** `null` when `geoResult` is `not_captured` (no coordinates to measure from). */
    distanceM: z.number().nullable(),
    exceptionsRaised: z.array(ExceptionTypeSchema),
    /** Present and `true` only when this call replayed an existing `clientPunchUuid` (I-6). */
    idempotentReplay: z.boolean().optional(),
});
/**
 * `source` and `punchId` are additive (Phase 3 batch, 2026-08-28).
 *
 * `punchId` on a *list* route is not redundant with `GET /punches/:id`: the
 * admin app pivots from an exception (which carries a `punchId`) into the
 * punch list already filtered, and gets back the same row shape the rest of
 * the screen renders instead of the detail shape.
 */
export const PunchListQuerySchema = DiscoveryQuerySchema.extend(PaginationQuerySchema.shape).extend({
    source: PunchSourceSchema.optional(),
    punchId: UuidSchema.optional(),
    /**
     * Both punches of one `punch_sessions` row (additive, 2026-09-01).
     *
     * The admin app's punch detail is a *session* surface — a supervisor asks
     * "what time did they arrive and leave", never "what does punch row 4f2 say"
     * — and `sessionId` on `PunchSchema` already reports the pairing but gave no
     * way to ask for the other half of it. Without this filter the only way to
     * reach the counterpart was to page the punch stream around the night and
     * re-derive the pairing, which is precisely what that field's own note says
     * clients must stop doing.
     */
    sessionId: UuidSchema.optional(),
    /**
     * Every punch of one shift (additive, 2026-09-04). The shift dialog is the
     * surface a supervisor is already on when the question is "what did this
     * shift actually cost" — the answer is its punches, and paging the stream
     * around the night to find them is the same re-derivation `sessionId` ended.
     */
    shiftId: UuidSchema.optional(),
});
export const PunchSchema = z.object({
    id: UuidSchema,
    clientPunchUuid: UuidSchema,
    userId: UuidSchema,
    shiftId: UuidSchema.nullable(),
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable(),
    deviceId: UuidSchema.nullable(),
    type: PunchTypeSchema,
    source: PunchSourceSchema,
    recordedAt: IsoDateTimeSchema,
    deviceRecordedAt: IsoDateTimeSchema.nullable(),
    monotonicElapsedMs: z.number().nullable(),
    clockOffsetMs: z.number().nullable(),
    lastServerContactAt: IsoDateTimeSchema.nullable(),
    clockDriftMs: z.number().nullable(),
    serverReceivedAt: IsoDateTimeSchema,
    gpsStatus: GpsStatusSchema,
    latitude: z.number().min(-90).max(90).nullable(),
    longitude: z.number().min(-180).max(180).nullable(),
    accuracyM: z.number().min(0).nullable(),
    distanceM: z.number().nullable(),
    geoResult: GeoResultSchema,
    /**
     * Phase 1 change batch (2026-08-27), Track C: the time actually used for
     * payroll after adjustments (`recordedAt` stays the original captured
     * value; `effectiveRecordedAt` reflects the latest `amend_time`/
     * `close_open` adjustment, if any).
     */
    effectiveRecordedAt: IsoDateTimeSchema,
    /** `true` when at least one non-voided adjustment applies to this punch. */
    isAdjusted: z.boolean(),
    /**
     * The `punch_sessions` row this punch belongs to — as `punch_in_id` or as
     * `punch_out_id` (Phase 3 batch, 2026-08-28).
     *
     * `punch_sessions` is the *only* place the IN↔OUT pairing is encoded
     * (02-DATA-MODEL.md §6), and it is not derivable from the punch stream: a
     * worker can have an unpaired IN, a supervisor-created OUT, or an offline
     * batch that arrives out of order. Every client that paired rows itself —
     * "next OUT after this IN" — was re-implementing that rule against a page
     * of punches it could only partly see, and getting a different answer from
     * the timesheet for exactly the sessions that matter. Reporting the id ends
     * that.
     *
     * `null` when no session references it, which for an IN means it never
     * opened one and for an OUT means it closed nothing. `.optional()` so a
     * reader still validates against a deployment that has not adopted it.
     */
    sessionId: UuidSchema.nullable().optional(),
});
//# sourceMappingURL=punch.js.map